import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { Maximize2, Link2, ArrowLeft } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { Button } from "@/components/ui/button";

const TONE = { active: ["ACTIVE", "border-health/50 bg-health/10", "text-health"], suspended: ["SUSPENDED — STOP WORK", "border-alert bg-alert/15 animate-pulse", "text-alert"], approved: ["AUTHORISED · STARTS LATER", "border-sky-400/40 bg-sky-400/10", "text-sky-300"], pending_approval: ["AWAITING AUTHORISATION", "border-[#FFA000]/40 bg-[#FFA000]/10", "text-[#FFA000]"] };
const time = (iso) => new Date(iso).toLocaleString("en-GB", { weekday: "short", hour: "2-digit", minute: "2-digit" });
const left = (iso, now) => {
  const m = Math.round((new Date(iso) - now) / 60000);
  if (m < 0) return "ended";
  return m >= 60 ? `${Math.floor(m / 60)}h ${m % 60}m left` : `${m}m left`;
};

const Board = ({ data, now }) => (
  <div className="min-h-screen bg-navy p-6 text-white sm:p-10" data-testid="permit-board">
    <div className="mb-8 flex flex-wrap items-end justify-between gap-4">
      <div><p className="font-mono text-xs uppercase tracking-widest text-health">Live permit board</p><h1 className="mt-1 font-display text-3xl font-bold sm:text-4xl">{data.company}</h1></div>
      <div className="text-right"><p className="font-display text-4xl font-bold tabular-nums" data-testid="board-clock">{now.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" })}</p><p className="text-sm text-slate-400">{now.toLocaleDateString("en-GB", { weekday: "long", day: "numeric", month: "long" })}</p></div>
    </div>
    <div className="mb-6 flex flex-wrap gap-6 font-mono text-sm">{["active", "suspended", "approved", "pending_approval"].map((s) => <span key={s} className={TONE[s][2]} data-testid={`board-count-${s}`}>{data.permits.filter((p) => p.status === s).length} {TONE[s][0].split(" ")[0].toLowerCase()}</span>)}</div>
    {data.permits.length === 0 ? <p className="py-24 text-center text-xl text-slate-400">No active, suspended or pending permits right now.</p> : (
      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">{data.permits.map((p) => (
        <div key={p.ref} className={`rounded-2xl border-2 p-5 ${TONE[p.status][1]}`} data-testid={`board-card-${p.ref}`}>
          <div className="flex items-center justify-between"><span className="font-mono text-sm text-slate-300">{p.ref}</span><span className={`font-mono text-xs font-bold tracking-wider ${TONE[p.status][2]}`}>{TONE[p.status][0]}</span></div>
          <p className="mt-3 font-display text-xl font-bold leading-tight">{p.title}</p>
          <p className="mt-1 text-sm text-slate-300">{p.type} · {p.location || "Location not set"}</p>
          <div className="mt-4 flex items-end justify-between text-sm"><div className="text-slate-400"><p>{time(p.valid_from)} → {time(p.valid_to)}</p><p>{p.approved_by ? `Authorised by ${p.approved_by}` : "Not yet authorised"}</p></div>
            <span className="font-mono text-lg font-semibold tabular-nums">{left(p.valid_to, now)}</span></div>
        </div>))}</div>
    )}
    <p className="mt-10 text-center text-xs text-slate-500">Refreshes every 30 seconds · Only work under an ACTIVE permit may proceed · Powered by Saflr</p>
  </div>
);

const useBoard = (url) => {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [now, setNow] = useState(new Date());
  useEffect(() => {
    const load = () => api.get(url).then((r) => { setData(r.data); setErr(null); }).catch((e) => setErr(errMsg(e)));
    load();
    const t1 = setInterval(load, 30000);
    const t2 = setInterval(() => setNow(new Date()), 15000);
    return () => { clearInterval(t1); clearInterval(t2); };
  }, [url]);
  return { data, err, now };
};

export const PublicPermitBoard = () => {
  const { token } = useParams();
  const { data, err, now } = useBoard(`/public/permit-board/${token}`);
  if (err && !data) return <div className="grid min-h-screen place-items-center bg-navy text-slate-300" data-testid="board-error">{err}</div>;
  if (!data) return <div className="min-h-screen bg-navy" />;
  return <Board data={data} now={now} />;
};

export default function PermitBoard() {
  const { data, now } = useBoard("/permits-board");
  const [link, setLink] = useState(null);
  if (!data) return null;
  const make = async () => {
    try {
      const { data: d } = await api.post("/permits-board/display-link");
      const url = `${window.location.origin}/board/${d.token}`;
      setLink(url);
      navigator.clipboard?.writeText(url);
      toast.success("Display link created and copied. Any previous link stopped working.");
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const revoke = async () => {
    await api.delete("/permits-board/display-link");
    setLink(null);
    toast.success("Display link revoked");
  };
  return (
    <div className="-mx-4 -my-8 sm:-mx-8">
      <div className="flex flex-wrap items-center gap-2 border-b bg-background px-6 py-3">
        <Link to="/app/permits" className="mr-auto inline-flex items-center gap-1 text-sm text-muted-foreground"><ArrowLeft className="h-4 w-4" />Permits</Link>
        <Button size="sm" variant="outline" onClick={() => document.getElementById("board-wrap")?.requestFullscreen?.()} data-testid="board-fullscreen"><Maximize2 className="mr-1.5 h-4 w-4" />Full screen</Button>
        {data.can_manage && <Button size="sm" variant="outline" onClick={make} data-testid="board-make-link"><Link2 className="mr-1.5 h-4 w-4" />{data.has_display_link ? "New display link" : "Create display-screen link"}</Button>}
        {data.can_manage && (data.has_display_link || link) && <Button size="sm" variant="ghost" onClick={revoke} data-testid="board-revoke-link">Revoke link</Button>}
      </div>
      {link && <p className="border-b bg-health/10 px-6 py-2 text-xs" data-testid="board-link">Open on a site screen (no sign-in needed, shows permit reference, type, location, times and authoriser only): <span className="break-all font-mono">{link}</span></p>}
      <div id="board-wrap"><Board data={data} now={now} /></div>
    </div>
  );
}
